import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  randomPickerColor,
  toastManager,
  VisualPicker,
  type Color,
  type GlyphName,
} from "@salestrak/ui";

import { WORKFLOW_DEFAULT_GLYPH } from "#app/lib/entity-visuals";
import { useWorkflowMutations } from "#app/lib/hooks/use-workflow-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CreateWorkflowDialogProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (workflowId: string) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 64;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateWorkflowDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateWorkflowDialogProps) {
  const { createWorkflow } = useWorkflowMutations();

  const [name, setName] = useState("");
  const [iconName, setIconName] = useState<GlyphName>(WORKFLOW_DEFAULT_GLYPH);
  // Random palette color (greys excluded) so consecutive workflows do not
  // all wear one hue; the picker overrides it.
  const [iconColor, setIconColor] = useState<Color>(() => randomPickerColor());

  const [nameError, setNameError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset on close — re-rolling the color so the next create doesn't
  // inherit this one's swatch (randomPickerColor excludes the previous pick).
  useEffect(() => {
    if (!open) {
      setName("");
      setIconName(WORKFLOW_DEFAULT_GLYPH);
      setIconColor((prev) => randomPickerColor(prev));
      setNameError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setNameError("");
  }, []);

  const canSubmit =
    name.trim().length >= NAME_MIN_LENGTH &&
    name.trim().length <= NAME_MAX_LENGTH &&
    !submitting;

  const handleSubmit = useCallback(async () => {
    if (name.trim().length < NAME_MIN_LENGTH) {
      setNameError(`Name must be at least ${NAME_MIN_LENGTH} characters`);
      return;
    }
    if (name.trim().length > NAME_MAX_LENGTH) {
      setNameError(`Name must be at most ${NAME_MAX_LENGTH} characters`);
      return;
    }

    setSubmitting(true);
    try {
      // No key: nothing addresses a workflow by one, so a new workflow
      // carries none. The settings page's Identifier row is where one is
      // set when it is wanted.
      const result = await createWorkflow({
        name: name.trim(),
        icon: { icon: iconName, color: iconColor },
      });

      if (!result) {
        toastManager.add({ title: "Failed to create workflow", type: "error" });
        return;
      }

      if ("__typename" in result) {
        if (result.__typename === "ValidationError") {
          const msg = result.message || "Validation error";
          toastManager.add({ title: msg, type: "error" });
          return;
        }
        if (result.__typename === "PermissionDenied") {
          toastManager.add({
            title: "You don't have permission to create workflows",
            type: "error",
          });
          return;
        }
      }

      if ("workflowId" in result) {
        onOpenChange(false);
        onCreated?.(result.workflowId);
      }
    } catch (err) {
      toastMutationError(err, "Failed to create workflow");
    } finally {
      setSubmitting(false);
    }
  }, [name, iconName, iconColor, createWorkflow, onOpenChange, onCreated]);

  // One Dialog; on a phone a sheet with Create on its title row.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup
        size="md"
        dialogOptions={{
          primary: { label: "Create", onClick: handleSubmit, disabled: !canSubmit, loading: submitting },
          cancel: { disabled: submitting },
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Workflow</DialogTitle>
        </DialogHeader>
        {/* DialogPanel owns the body gutters and the header/footer spacing
            rules — a bare padded Box leaves no gap above the footer. */}
        <DialogPanel>
          <Box
            as="form"
            gap="lg"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) void handleSubmit();
            }}
          >
            <Field invalid={!!nameError}>
              <FieldLabel>Icon &amp; name</FieldLabel>
              {/* Field's root is items-start, so a bare row hugs its content —
                  the row must claim the column width for the input to span. */}
              <Box direction="row" gap="sm" align="center" fill="width">
                {/* The settings page's Name & Icon trigger: an input hairline
                    over the dialog surface with the glyph in the chosen color
                    — no painted tile, the same face the workflow wears in
                    its rows. Reset returns the glyph only; the seeded color
                    stays, because a fresh random hue would read as a second,
                    unasked-for change. */}
                <VisualPicker
                  icon={iconName}
                  defaultIcon={WORKFLOW_DEFAULT_GLYPH}
                  color={iconColor}
                  onIconChange={setIconName}
                  onColorChange={setIconColor}
                  // Offered only while the glyph has left its default: a
                  // Reset that would change nothing is noise.
                  onReset={
                    iconName === WORKFLOW_DEFAULT_GLYPH
                      ? undefined
                      : () => setIconName(WORKFLOW_DEFAULT_GLYPH)
                  }
                  size="sm"
                  variant="outline"
                />
                <Box fill="flex" shrink>
                  <Input
                    placeholder="e.g. Sales Pipeline"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    maxLength={NAME_MAX_LENGTH}
                    autoFocus
                  />
                </Box>
              </Box>
              {nameError ? <FieldError match>{nameError}</FieldError> : null}
            </Field>
          </Box>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}
